import Page from "@/components/Page"
import Spinner from "@/components/Spinner"
import { Skeleton } from "@/components/chadcn/Skeleton"
import { useSelfQuery } from "@/data/queries"
import {
  useLastBloodGlucoseObservationWithReadingQuery,
  usePatientObservationsQuery,
} from "@/data/queries/measurementQueries"
import { BloodGlucoseObservationWithReading } from "@/model"
import { ROUTES } from "@/routes"
import { DayOfWeek, LocalDate, ZonedDateTime, ZoneId } from "@js-joda/core"
import { useState } from "react"
import { useLocation, useNavigate, useSearchParams } from "react-router"
import { toast } from "sonner"
import DashboardTabs from "./components/DashboardTabs"
import EmptyDashboard from "./components/EmptyDashboard"
import FoodLogList from "./components/FoodLogList"
import LastReadingCard from "./components/LastReadingCard"
import MeasurementGrid from "./components/MeasurementGrid"
import MeasurementsDashboard from "./components/MeasurementsDashboard"
import NewMeasurementModal from "./components/NewMeasurementModal"
import ThresholdLegend from "./components/ThresholdLegend"
import WeeklyCalendar from "./components/WeeklyCalendar"
import {
  type DashboardTab,
  isDashboardTab,
  measurementsByDate,
  overviewReadings,
} from "./dashboardData"

const currentWeekSunday = (): ZonedDateTime => {
  const today = LocalDate.now(ZoneId.systemDefault())
  const daysFromSunday = today.dayOfWeek().value() % DayOfWeek.SUNDAY.value()
  return today.minusDays(daysFromSunday).atStartOfDay(ZoneId.systemDefault())
}

const DashboardPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const { data: self, isPending: isSelfPending } = useSelfQuery()
  const patientId = self?.id
  const patientHasReadings = self?.status === "monitoring"
  const tabParam = searchParams.get("tab")
  const activeTab: DashboardTab = isDashboardTab(tabParam) ? tabParam : "overview"
  const initialLogin = (location.state as { initialLogin?: boolean } | null)?.initialLogin

  // Blood-glucose contextualization SMS deep-links land at /dashboard?measurementId=<id>
  // (production URL shape — see backend BloodGlucoseReadingContextualizationUrlFor.kt).
  // We open NewMeasurementModal in contextualization mode so the patient picks
  // the form context (after meal / fasting / quick check) for that reading.
  const measurementIdParam = searchParams.get("measurementId")
  const dashboardTabUrl = (tab: DashboardTab = activeTab) => `${ROUTES.DASHBOARD}?tab=${tab}`
  const dismissContextualizationModal = () =>
    navigate(dashboardTabUrl(activeTab), { replace: true })

  const [weekStart, setWeekStart] = useState<ZonedDateTime>(currentWeekSunday)
  const weekEnd = weekStart.plusDays(7).minusNanos(1)

  const {
    data: observations,
    isPending,
    isError,
    refetch,
  } = usePatientObservationsQuery({ patientId, from: weekStart, to: weekEnd })

  const { data: lastBloodGlucoseObservationWithReading } =
    useLastBloodGlucoseObservationWithReadingQuery({ patientId })

  const handleSwitchTabs = (tab: DashboardTab) => {
    navigate(dashboardTabUrl(tab))
  }

  const handleReadingCardClick = (observation: BloodGlucoseObservationWithReading) => {
    const { form, reading, isFormEditable } = observation
    const isContextualized = form?.context !== undefined

    if (!isFormEditable && !isContextualized) {
      toast.warning("Unable to edit measurement", {
        description:
          "Your doctor has already reviewed your measurement. It is no longer possible to make changes.",
      })
      return
    }

    if (isContextualized) {
      navigate(
        form.context === "quick-check"
          ? buildQuickCheckEditPath(reading.id)
          : buildMeasurementEditPath(reading.id),
        { state: { initialTab: activeTab } },
      )
      return
    }

    navigate(`${ROUTES.DASHBOARD}?tab=${activeTab}&measurementId=${reading.id}`)
  }

  const weekOverviewReadings = observations
    ? overviewReadings(observations.observations, weekStart)
    : []
  const weekMeasurementsByDate = observations ? measurementsByDate(observations.observations) : {}

  if (isSelfPending) {
    return (
      <Page>
        <div className="flex justify-center py-12">
          <Spinner />
        </div>
      </Page>
    )
  }

  if (isPending) {
    return (
      <Page>
        <DashboardLoadingSkeleton />
      </Page>
    )
  }

  if (isError || !observations) {
    return (
      <Page>
        <EmptyDashboard message="Couldn't load your readings." onRetry={() => refetch()} />
      </Page>
    )
  }

  return (
    <Page>
      <LastReadingCard
        observation={lastBloodGlucoseObservationWithReading}
        onClick={handleReadingCardClick}
      />
      {!patientHasReadings ? (
        <EmptyDashboard />
      ) : (
        <>
          <WeeklyCalendar weekStart={weekStart} onWeekChange={setWeekStart} />
          <DashboardTabs value={activeTab} onChange={handleSwitchTabs} />
          {activeTab === "overview" && (
            <>
              <MeasurementGrid
                readings={weekOverviewReadings}
                statistics={observations.statistics}
              />
              <ThresholdLegend />
            </>
          )}
          {activeTab === "meal-logs" && <FoodLogList observations={observations.observations} />}
          {activeTab === "measurement" && (
            <MeasurementsDashboard
              observationsByDate={weekMeasurementsByDate}
              onReadingCardClick={handleReadingCardClick}
            />
          )}
        </>
      )}

      {measurementIdParam ? (
        <NewMeasurementModal
          measurementId={measurementIdParam}
          onClose={dismissContextualizationModal}
        />
      ) : patientHasReadings && initialLogin ? (
        <NewMeasurementModal
          onClose={() => navigate(dashboardTabUrl("overview"), { replace: true })}
        />
      ) : null}
    </Page>
  )
}

// Mirrors the old app's per-section MUI Skeletons: last-reading card,
// week bar, tabs, median stat cards, and one row per day of the week.
const DashboardLoadingSkeleton = () => (
  <div aria-label="Loading dashboard" className="flex flex-col gap-4">
    <Skeleton className="h-24 w-full" />
    <Skeleton className="h-10 w-full" />
    <Skeleton className="h-9 w-full" />
    <div className="flex gap-2">
      {["fasting", "breakfast", "lunch", "dinner"].map((context) => (
        <Skeleton key={context} className="h-16 flex-1" />
      ))}
    </div>
    <div className="flex flex-col gap-2">
      {Array.from({ length: 7 }, (_, dayIndex) => (
        <Skeleton key={dayIndex} className="h-9 w-full" />
      ))}
    </div>
  </div>
)

const buildMeasurementEditPath = (measurementId: string) =>
  ROUTES.MEASUREMENT.EDIT.replace(":measurementId", measurementId)

const buildQuickCheckEditPath = (measurementId: string) =>
  ROUTES.QUICK_CHECK_EDIT.replace(":measurementId", measurementId)

export default DashboardPage
